import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaCog, FaFilter, FaSearch, FaTimes } from 'react-icons/fa';
import utilitiesAPI from '../api/utilitiesAPI';
import { useUtilitiesCompany } from '../context/UtilitiesCompanyContext';
import '../styles/utilitiesFormOdoo.css';
import { RESOURCES, resourceAllowedForCompany } from '../utils/utilitiesResources';
import { formatDate, formatMoney, isUnauthorized, many2oneName, pickRecords, pickTotal } from '../utils/utilitiesFormat';

const PAGE_SIZE = 80;
const NO_FILTERS = { states: [], search: '', date_from: '', date_to: '' };

function cellValue(record, column) {
  const raw = record?.[column.key];
  if (column.money) return formatMoney(raw ?? 0);
  if (column.date) return formatDate(raw);
  return many2oneName(raw);
}

function Facet({ icon, text, onRemove }) {
  return (
    <span className="o-facet">
      <span className="o-facet__icon">{icon}</span>
      <span className="o-facet__text">{text}</span>
      <button type="button" className="o-facet__remove" aria-label={`Remove ${text}`} onClick={onRemove}>
        <FaTimes />
      </button>
    </span>
  );
}

export default function UtilitiesListPage() {
  const { resource: resourceKey } = useParams();
  const navigate = useNavigate();
  const resource = RESOURCES[resourceKey];
  const { companies, company, companyId, setCompanyId } = useUtilitiesCompany();

  const [filters, setFilters] = useState(NO_FILTERS);
  const [searchText, setSearchText] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const searchRef = useRef(null);

  const notAllowed = Boolean(resource && company && !resourceAllowedForCompany(resource, company));

  const load = useCallback(async () => {
    if (!resource || !companyId || notAllowed) return;
    setLoading(true);
    setSelected([]);
    try {
      const params = { limit: PAGE_SIZE, offset };
      // Odoo treats two filters of one group as "either", which is the same as no state filter.
      if (filters.states.length === 1) params.state = filters.states[0];
      if (filters.search) params.search = filters.search;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await utilitiesAPI.list(resource.key, companyId, params);
      const rows = pickRecords(res, resource.key);
      setRecords(rows);
      setTotal(pickTotal(res, rows.length));
    } catch (err) {
      if (isUnauthorized(err)) {
        navigate('/bu-dashboard/login', { replace: true });
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [resource, companyId, notAllowed, filters, offset, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Another form or company starts from the first page with no filters.
  useEffect(() => {
    setFilters(NO_FILTERS);
    setSearchText('');
    setOffset(0);
    setPanelOpen(false);
    setMenuOpen(false);
  }, [resourceKey, companyId]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const close = (e) => {
      if (!searchRef.current?.contains(e.target)) setPanelOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [panelOpen]);

  if (!resource) return <Navigate to="/bu-dashboard/utilities/dashboard" replace />;

  const updateFilters = (patch) => {
    setOffset(0);
    setFilters((f) => ({ ...f, ...patch }));
  };

  const toggleState = (value) => {
    const states = filters.states.includes(value) ? filters.states.filter((s) => s !== value) : [...filters.states, value];
    updateFilters({ states });
  };

  const stateLabel = (value) => resource.states.find((s) => s.value === value)?.label || value;
  const badgeClass = (value) => (value === resource.states[0].value ? 'o-badge--info' : 'o-badge--success');

  const allSelected = records.length > 0 && selected.length === records.length;
  const toggleRow = (rowId) =>
    setSelected((cur) => (cur.includes(rowId) ? cur.filter((v) => v !== rowId) : [...cur, rowId]));

  const deleteSelected = async () => {
    setMenuOpen(false);
    if (!window.confirm(`Are you sure you want to delete ${selected.length === 1 ? 'this record' : `these ${selected.length} records`}?`)) return;
    try {
      for (const rowId of selected) {
        // eslint-disable-next-line no-await-in-loop
        await utilitiesAPI.remove(resource.key, companyId, rowId);
      }
    } catch (err) {
      setError(err.message);
    }
    load();
  };

  const hasFilters = Boolean(filters.states.length || filters.search || filters.date_from || filters.date_to);
  const first = total ? offset + 1 : 0;
  const last = Math.min(offset + PAGE_SIZE, total);
  const colCount = resource.listColumns.length + 2;
  const hasSum = resource.listColumns.some((c) => c.sum);
  const sumOf = (column) => records.reduce((acc, row) => acc + (Number(row[column.key]) || 0), 0);

  return (
    <div className="o-utilities-form o-utilities-tree">
      <div className="o-control-panel">
        <div className="o-cp-left">
          {!notAllowed && (
            <button type="button" className="o-btn o-btn--primary" onClick={() => navigate(`/bu-dashboard/utilities/${resource.key}/new`)}>
              New
            </button>
          )}
          <span className="o-cp-title">{resource.formTitle}</span>
          {selected.length > 0 && (
            <>
              <span className="o-cp-selected">{selected.length} selected</span>
              <span className="o-menu">
                <button
                  type="button"
                  className="o-icon-btn o-cp-gear"
                  aria-label="Actions menu"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((m) => !m)}
                >
                  <FaCog />
                </button>
                {menuOpen && (
                  <div className="o-menu__list" role="menu">
                    <button type="button" role="menuitem" className="o-menu__item" onClick={deleteSelected}>
                      Delete
                    </button>
                  </div>
                )}
              </span>
            </>
          )}
        </div>

        {!notAllowed && (
          <div className="o-search" role="search" ref={searchRef}>
            <div className="o-search__box">
              <FaSearch className="o-search__icon" />
              {filters.states.length > 0 && (
                <Facet
                  icon={<FaFilter />}
                  text={filters.states.map(stateLabel).join(' or ')}
                  onRemove={() => updateFilters({ states: [] })}
                />
              )}
              {(filters.date_from || filters.date_to) && (
                <Facet
                  icon={<FaFilter />}
                  text={`${formatDate(filters.date_from) || '…'} - ${formatDate(filters.date_to) || '…'}`}
                  onRemove={() => updateFilters({ date_from: '', date_to: '' })}
                />
              )}
              {filters.search && (
                <Facet
                  icon={<FaSearch />}
                  text={filters.search}
                  onRemove={() => {
                    updateFilters({ search: '' });
                  }}
                />
              )}
              <input
                type="text"
                className="o-search__input"
                placeholder="Search..."
                aria-label="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    updateFilters({ search: searchText.trim() });
                    setSearchText('');
                  } else if (e.key === 'Backspace' && !searchText && filters.search) {
                    updateFilters({ search: '' });
                  }
                }}
              />
            </div>
            <button
              type="button"
              className="o-search__toggle"
              aria-label="Toggle Search Panel"
              aria-expanded={panelOpen}
              onClick={() => setPanelOpen((p) => !p)}
            />
            {panelOpen && (
              <div className="o-search__panel">
                <div className="o-search__heading">Filters</div>
                {resource.states.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    aria-pressed={filters.states.includes(s.value)}
                    className={`o-search__option${filters.states.includes(s.value) ? ' is-on' : ''}`}
                    onClick={() => toggleState(s.value)}
                  >
                    {s.label}
                  </button>
                ))}
                <div className="o-search__dates">
                  <label>
                    From
                    <input type="date" value={filters.date_from} onChange={(e) => updateFilters({ date_from: e.target.value })} />
                  </label>
                  <label>
                    To
                    <input type="date" value={filters.date_to} onChange={(e) => updateFilters({ date_to: e.target.value })} />
                  </label>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="o-cp-right">
          {companies.length > 1 && (
            <select
              className="o-company-select"
              aria-label="Company"
              value={companyId ?? ''}
              onChange={(e) => setCompanyId(e.target.value)}
            >
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          {!notAllowed && total > 0 && (
            <nav className="o-pager" aria-label="Pager">
              <span>
                {first}-{last} / {total}
              </span>
              <span className="o-pager__buttons">
                <button
                  type="button"
                  className="o-pager__btn"
                  aria-label="Previous"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  <FaChevronLeft />
                </button>
                <button
                  type="button"
                  className="o-pager__btn"
                  aria-label="Next"
                  disabled={last >= total || loading}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  <FaChevronRight />
                </button>
              </span>
            </nav>
          )}
        </div>
      </div>

      {error && (
        <div className="o-toast" role="alert">
          <span>{error}</span>
          <button type="button" aria-label="Close" onClick={() => setError(null)}>
            <FaTimes />
          </button>
        </div>
      )}

      {notAllowed ? (
        <div className="o-alert" role="status">
          {resource.formTitle} is not available in {company.name}. Please switch company.
        </div>
      ) : (
        <div className="o-tree-wrap">
          <table className="o-tree">
            <thead>
              <tr>
                <th className="o-tree__select">
                  <input
                    type="checkbox"
                    className="o-checkbox"
                    aria-label="Select all records"
                    checked={allSelected}
                    onChange={() => setSelected(allSelected ? [] : records.map((r) => r.id))}
                  />
                </th>
                {resource.listColumns.map((c) => (
                  <th key={c.key} className={c.money ? 'o-num' : undefined}>
                    {c.label}
                  </th>
                ))}
                <th>Status</th>
              </tr>
            </thead>
            {records.length > 0 && (
              <tbody>
                {records.map((row) => (
                  <tr key={row.id} onClick={() => navigate(`/bu-dashboard/utilities/${resource.key}/${row.id}`)}>
                    <td className="o-tree__select" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="o-checkbox"
                        aria-label="Select record"
                        checked={selected.includes(row.id)}
                        onChange={() => toggleRow(row.id)}
                      />
                    </td>
                    {resource.listColumns.map((c) => (
                      <td key={c.key} className={c.money ? 'o-num' : undefined} title={c.money ? undefined : String(cellValue(row, c))}>
                        {cellValue(row, c)}
                      </td>
                    ))}
                    <td>
                      <span className={`o-badge ${badgeClass(row.state)}`}>{row.state_label || stateLabel(row.state)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
            {records.length > 0 && hasSum && (
              <tfoot>
                <tr>
                  <td />
                  {resource.listColumns.map((c) => (
                    <td key={c.key} className={c.money ? 'o-num' : undefined}>
                      {c.sum ? <span title="Total">{formatMoney(sumOf(c))}</span> : null}
                    </td>
                  ))}
                  <td />
                </tr>
              </tfoot>
            )}
            {records.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={colCount} style={{ height: 0, padding: 0, border: 0 }} />
                </tr>
              </tbody>
            )}
          </table>

          {records.length === 0 && !loading && (
            <div className="o-nocontent">
              {hasFilters ? (
                <>
                  <p className="o-nocontent__title">No record found</p>
                  <p>Adjust your filters or create a new record.</p>
                </>
              ) : (
                <>
                  <p className="o-nocontent__title">{resource.emptyHelp[0]}</p>
                  {resource.emptyHelp.slice(1).map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
